import { ComponentProps } from "react";
import { faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Options,
  Splide,
  SplideSlide,
  SplideTrack,
} from "@greglaisph/react-splide";
import { useWindowSize } from "@hooks/useWindowSize";
import Card from "@particles/card/Card";
import { CardVariations } from "@particles/card/utils/types";
import { getSliderCardSize } from "@routes/Keyword/templates/CampaignTemplate/utils/helper";
import { Grid } from "@splidejs/splide-extension-grid";
import { cn } from "@util/helpers";

import "@greglaisph/react-splide/css";

type PodcastSliderProps = {
  /**List of articles to display in podcasts section*/
  cardProps: ComponentProps<typeof Card>[];
};

/**
 * Podcast cards with slider that is displayed in Podcasts section
 * https://www.figma.com/design/QCEPjsj7Sdm5KqnY3ta5tq/TEMPLATES--WEB---APP-?node-id=1-12583&m=dev
 */
const PodcastSlider = ({
  cardProps,
}: PodcastSliderProps): React.ReactElement => {
  const { isScreenLG, isScreenMD } = useWindowSize();

  const isBasicCard = cardProps.length <= 2;

  const options: Options = {
    pagination: false,
    interval: 10000,
    gap: "1.5rem",
    focus: 0,
    omitEnd: true,
    mediaQuery: "min",
    perPage: 1,
    fixedWidth: isBasicCard
      ? "100%"
      : getSliderCardSize(isScreenLG, isScreenMD),
    arrows: !isBasicCard && isScreenLG,
    grid: { rows: 2, cols: 1, gap: { row: "1rem", col: "1rem" } },
    breakpoints: {
      768: isBasicCard ? { grid: null, fixedWidth: "50%" } : {},
      992: isBasicCard ? { perPage: 4, perMove: 4 } : {},
      1200: isBasicCard ? { perPage: 4, perMove: 4 } : {},
    },
  };

  const variant: CardVariations = {
    image: {
      position: "left",
      width: cn("w-3/10 md:w-1/2 lg:w-3/10", {
        "w-full md:w-full lg:w-full": !isBasicCard,
      }),
      imageWrapperClass: "aspect-3x2",
      imageClassName: "object-cover",
    },
    content: {
      width: "w-7/10 md:w-1/2 lg:w-7/10",
      extraClass: cn({ "pl-4": isBasicCard }),
    },
    title: {
      size: "text-base md:text-lg",
      color: "text-gray-850",
      extraClass: cn("font-lct", { "pt-2": !isBasicCard }),
    },
  };

  return (
    <div data-testid="podcast-slider-component">
      <Splide
        options={options}
        extensions={isBasicCard ? { Grid } : undefined}
        tag="div"
        hasTrack={false}
        className={cn({ "relative md:pr-9": isBasicCard })}
        data-testid="splide"
      >
        <div className="splide__arrows absolute bottom-full right-0 flex gap-2">
          <button
            className="splide__arrow splide__arrow--prev !static !bg-white"
            data-testid="splide-prev"
          >
            <FontAwesomeIcon
              icon={faChevronRight}
              size="sm"
              className="block !h-4 !w-4 cursor-pointer rounded-full border border-black fill-gray-650 p-1.5 hover:opacity-100"
            />
          </button>

          <button
            className="splide__arrow splide__arrow--next !static !bg-white"
            data-testid="splide-next"
          >
            <FontAwesomeIcon
              icon={faChevronRight}
              size="sm"
              className="block !h-4 !w-4 cursor-pointer rounded-full border border-black fill-gray-650 p-1.5 hover:opacity-100"
            />
          </button>
        </div>

        <SplideTrack data-testid="splide-track">
          {cardProps.map((card) => {
            return (
              <SplideSlide key={card.id} data-testid="splide-slide">
                <Card {...card} variations={variant} className="mt-4 md:mt-0" />
              </SplideSlide>
            );
          })}
        </SplideTrack>
      </Splide>
    </div>
  );
};

export default PodcastSlider;
